/**
 * InProgressJobsTab — the "In Progress" tab body of the Jobs page,
 * extracted from JobsEnhanced.jsx (Jun 2026) to shrink that file.
 * All state and handlers stay in the parent and arrive as props.
 */
import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import Pagination from '../Pagination';
import LabelChip from '../LabelChip';
import TeamWorkingNow from './TeamWorkingNow';
import { formatTimeWithTimezone } from '../../utils/timezone';
import { Briefcase, User, MapPin, Clock, Camera, UserCog, Pause, CheckCircle2, StickyNote, Wrench, ShieldCheck, Edit2, Archive, Trash2 } from 'lucide-react';
import JobAssignees from './JobAssignees';
import { TeamViaBadge } from './TeamViaBadge';

export const InProgressJobsTab = ({
  inProgressJobs,
  inProgressJobsPage,
  user,
  canManageJobs,
  canWorkJob,
  getUserName,
  getJobDuration,
  selectedJobIds,
  toggleSelectJob,
  unreadNotes,
  labelsById,
  setDetailJob,
  setWorkbenchJob,
  users = [],
  onCrewChanged,
  setQuickNoteJob,
  setSelectedJob,
  setShowReassignDialog,
  openPhotoGallery,
  handlePhotoUpload,
  openAdminPhotos,
  uploadingPhoto,
  openEditDialog,
  handleArchiveJob,
  handleDeleteJob,
  canDeleteJobs = false,
  openPauseModal,
  handleCompleteJob,
  isLoading,
  pages,
  setPages,
  pageSize,
  setPageSize,
}) => (
  <>
    {inProgressJobs.length === 0 ? (
      <Card className="border-2 border-dashed border-[#FFA000]">
        <CardContent className="p-12 text-center">
          <Briefcase className="w-16 h-16 mx-auto text-orange-400 mb-4" />
          <p className="text-slate-500 text-lg font-medium">No jobs in progress</p>
          <p className="text-gray-400 text-sm mt-2">Jobs will appear here when started</p>
        </CardContent>
      </Card>
    ) : (
      <div className="space-y-4">
        {inProgressJobsPage.map((job) => {
          const duration = getJobDuration(job.started_at, null);

          return (
            <Card key={job.id} className="relative shadow-md border-l-4 border-[#FFA000] bg-[#1A2433]">
              {canManageJobs && (
                <input
                  type="checkbox"
                  aria-label={`Select job ${job.job_number || job.title}`}
                  checked={selectedJobIds.has(job.id)}
                  onChange={() => toggleSelectJob(job.id)}
                  className="absolute top-3 right-3 w-4 h-4 cursor-pointer accent-blue-500 z-10"
                  data-testid={`select-job-${job.id}`}
                  title="Select for bulk action"
                />
              )}
              <CardContent className="p-5">
                <div className="space-y-3">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="text-lg font-bold text-white flex items-baseline gap-2 flex-wrap">{job.job_number && <span className="text-xs font-mono text-[#1E88E5] bg-[#1E88E5]/10 px-2 py-0.5 rounded" data-testid={`job-number-${job.id}`}>{job.job_number}</span>}<button type="button" onClick={(e) => { e.stopPropagation(); setDetailJob(job); }} className="text-left hover:text-blue-400 hover:underline transition-colors cursor-pointer" data-testid={`open-job-detail-${job.id}`} title="Open job details (notes, files, activity)">{job.title}</button>{unreadNotes[job.id] > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white animate-pulse" title={`${unreadNotes[job.id]} unread note${unreadNotes[job.id] === 1 ? '' : 's'}`} data-testid={`unread-notes-badge-${job.id}`}>{unreadNotes[job.id]} new</span>}{canManageJobs && <button type="button" onClick={(e) => { e.stopPropagation(); setWorkbenchJob(job); }} className="text-slate-500 hover:text-[#1E88E5] transition-colors" data-testid={`open-workbench-${job.id}`} title="Job Workbench — crew, time and costs"><Wrench className="w-4 h-4" /></button>}</h3><TeamViaBadge job={job} />
                        <Badge className="bg-[#0F1923]0 text-white">🚧 In Progress</Badge>
                        {job.is_extended && (
                          <Badge
                            className="border text-white"
                            style={{ background: 'rgba(139,92,246,0.15)', borderColor: '#8B5CF6' }}
                            data-testid={`extended-badge-${job.id}`}
                          >
                            Extended
                          </Badge>
                        )}
                        {job.photos_required && (
                          <Badge className="bg-orange-100 text-orange-800 border-[#FFA000] border">
                            <Camera className="w-3 h-3 mr-1" />
                            Photos Required
                          </Badge>
                        )}
                        {job.safety_attestation && (
                          <Badge
                            className="border text-emerald-50"
                            style={{ background: 'rgba(16,185,129,0.15)', borderColor: '#10B981' }}
                            title={`Safety acknowledged by ${job.safety_attestation.accepted_by_name || 'worker'} at ${new Date(job.safety_attestation.accepted_at).toLocaleString()}`}
                            data-testid={`safety-attested-badge-${job.id}`}
                          >
                            <ShieldCheck className="w-3 h-3 mr-1" />
                            Safety: {job.safety_attestation.accepted_by_name}
                            {job.safety_attestation.accepted_for_team && job.safety_attestation.team_name
                              ? ` (for ${job.safety_attestation.team_name})`
                              : ''}
                          </Badge>
                        )}
                      </div>
                      {(job.label_ids || []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`job-labels-row-${job.id}`}>
                          {(job.label_ids || []).map((lid) => labelsById[lid]).filter(Boolean).map((l) => (
                            <LabelChip key={l.id} label={l} size="sm" />
                          ))}
                        </div>
                      )}
                      {job.description && (
                        <p className="text-sm text-slate-400 mb-2">{job.description}</p>
                      )}
                    </div>
                  </div>

                  {/* Job Info Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm bg-[#1A2433] p-3 rounded-lg">
                    <JobAssignees job={job} getUserName={getUserName} canManage={canManageJobs} users={users} onChanged={onCrewChanged} iconClass="text-[#FFA000]" />
                    {job.job_address && (
                      <div className="flex items-center space-x-2 text-slate-300">
                        <MapPin className="w-4 h-4 text-[#FFA000]" />
                        <span className="font-medium">Location:</span>
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(job.job_address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#1E88E5] hover:underline truncate"
                        >
                          View on Map
                        </a>
                      </div>
                    )}
                    <div className="flex items-center space-x-2 text-slate-300">
                      <Clock className="w-4 h-4 text-[#FFA000]" />
                      <span className="font-medium">Started:</span>
                      <span>{formatTimeWithTimezone(job.started_at, user?.timezone || 'America/Toronto')}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-orange-700 font-semibold">
                      <Clock className="w-4 h-4 animate-pulse" />
                      <span className="font-medium">Duration:</span>
                      <span>{duration}</span>
                    </div>
                    {job.customer_name && (
                      <div className="flex items-center space-x-2 text-slate-300">
                        <User className="w-4 h-4 text-[#FFA000]" />
                        <span className="font-medium">Customer:</span>
                        <span>{job.customer_name}</span>
                      </div>
                    )}
                    {job.photos && job.photos.length > 0 && (
                      <div className="flex items-center space-x-2 text-slate-300">
                        <Camera className="w-4 h-4 text-[#FFA000]" />
                        <span className="font-medium">Photos:</span>
                        <button
                          onClick={() => openPhotoGallery(job)}
                          className="text-[#1E88E5] hover:underline font-medium"
                        >
                          {job.photos.length} uploaded
                        </button>
                      </div>
                    )}
                    {(job.started_on_behalf || job.completed_on_behalf || job.paused_on_behalf) && (
                      <div className="flex items-center space-x-2 text-amber-400 text-xs" data-testid={`on-behalf-audit-${job.id}`}>
                        <UserCog className="w-3.5 h-3.5" />
                        <span className="font-medium">
                          {job.completed_on_behalf
                            ? `Completed on behalf by ${job.completed_by_name || 'admin'}`
                            : job.paused_on_behalf
                              ? `Paused on behalf by ${job.paused_by_name || 'admin'}`
                              : `Started on behalf by ${job.started_by_name || 'admin'}`}
                        </span>
                      </div>
                    )}
                  </div>

                  <TeamWorkingNow job={job} getUserName={getUserName} currentUserId={user?.id} timezone={user?.timezone} onChanged={onCrewChanged} />

                  {/* Actions */}
                  {canWorkJob(job) && (
                    <div className="flex flex-wrap gap-2 pt-3 border-t">
                      <input
                        type="file"
                        id={`photo-upload-progress-${job.id}`}
                        accept="image/*"
                        multiple
                        onChange={(e) => handlePhotoUpload(e, job)}
                        className="hidden"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => document.getElementById(`photo-upload-progress-${job.id}`).click()}
                        disabled={uploadingPhoto || (job.photos && job.photos.length >= 10)}
                        className="border-[#FFA000] text-orange-700 hover:bg-[#0F1923]"
                      >
                        <Camera className="w-4 h-4 mr-1" />
                        Add Photos ({job.photos ? job.photos.length : 0}/10)
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setQuickNoteJob(job)}
                        className="border-amber-500 text-amber-300 hover:bg-amber-500/10"
                        data-testid={`add-note-job-${job.id}`}
                        title="Add a note to this job"
                      >
                        <StickyNote className="w-4 h-4 mr-1" />
                        Add a Note
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openPauseModal(job.id)}
                        disabled={isLoading}
                        className="border-[#FFA000] text-yellow-700 hover:bg-[#0F1923]"
                      >
                        <Pause className="w-4 h-4 mr-1" />
                        Pause Job
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleCompleteJob(job.id)}
                        disabled={isLoading}
                        className="bg-[#00C896] hover:bg-green-700 text-white"
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Complete Job
                      </Button>
                    </div>
                  )}

                  {/* Admin/Supervisor Actions for In Progress Jobs */}
                  {canManageJobs && !canWorkJob(job) && (
                    <div className="flex flex-wrap gap-2 pt-3 border-t">
                      <Button
                        size="sm"
                        onClick={() => handleCompleteJob(job.id)}
                        disabled={isLoading}
                        className="bg-[#00C896] hover:bg-green-700 text-white"
                        data-testid={`admin-complete-job-${job.id}`}
                        title={`Complete on behalf of ${getUserName(job.assigned_to)}`}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Complete (on behalf)
                      </Button>
                      <input
                        type="file"
                        id={`photo-upload-progress-admin-${job.id}`}
                        accept="image/*"
                        multiple
                        onChange={(e) => handlePhotoUpload(e, job)}
                        className="hidden"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => (openAdminPhotos ? openAdminPhotos(job) : document.getElementById(`photo-upload-progress-admin-${job.id}`).click())}
                        data-testid={`admin-add-photos-${job.id}`}
                        disabled={uploadingPhoto || (job.photos && job.photos.length >= 10)}
                        className="border-[#FFA000]/60 text-amber-300 hover:bg-[#0F1923] disabled:opacity-40"
                      >
                        <Camera className="w-4 h-4 mr-1" />
                        Add Photos ({job.photos ? job.photos.length : 0}/10)
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setQuickNoteJob(job)}
                        className="border-amber-500 text-amber-300 hover:bg-amber-500/10"
                        data-testid={`add-note-job-${job.id}`}
                        title="Add a note to this job"
                      >
                        <StickyNote className="w-4 h-4 mr-1" />
                        Add a Note
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openPauseModal(job.id)}
                        disabled={isLoading}
                        className="border-[#FFA000] text-yellow-700 hover:bg-[#0F1923]"
                        data-testid={`admin-pause-job-${job.id}`}
                        title={`Pause on behalf of ${getUserName(job.assigned_to)}`}
                      >
                        <Pause className="w-4 h-4 mr-1" />
                        Pause (on behalf)
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedJob(job);
                          setShowReassignDialog(true);
                        }}
                        className="border-[#2D3748] text-blue-700 hover:bg-[#0F1923]"
                        data-testid={`admin-reassign-job-${job.id}`}
                        title={`Reassign from ${getUserName(job.assigned_to)} to another user`}
                      >
                        <UserCog className="w-4 h-4 mr-1" />
                        Reassign
                      </Button>
                    </div>
                  )}

                  {/* Manage row — admins/supervisors can edit, archive or delete a job in ANY status (Oct 2026) */}
                  {canManageJobs && openEditDialog && (
                    <div className="flex flex-wrap gap-2 pt-3 border-t border-dashed border-[#2D3748]" data-testid={`manage-row-inprogress-${job.id}`}>
                      <Button size="sm" variant="outline" onClick={() => openEditDialog(job)} className="border-[#2D3748] text-slate-300 hover:bg-[#1A2433]" data-testid={`edit-job-inprogress-${job.id}`}>
                        <Edit2 className="w-4 h-4 mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleArchiveJob(job.id)} disabled={isLoading} className="border-[#FFA000] text-[#FFA000] hover:bg-[#FFA000]/10" data-testid={`archive-job-inprogress-${job.id}`} title="Archive for later (seasonal hold, cancelled, on hold) — restore any time">
                        <Archive className="w-4 h-4 mr-1" /> Archive
                      </Button>
                      {canDeleteJobs && (
                        <Button size="sm" variant="destructive" onClick={() => handleDeleteJob(job.id)} disabled={isLoading} className="ml-auto" data-testid={`delete-job-inprogress-${job.id}`}>
                          <Trash2 className="w-4 h-4 mr-1" /> Delete
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    )}
    <Pagination
      total={inProgressJobs.length}
      page={pages.inprogress}
      pageSize={pageSize}
      onPageChange={(p) => setPages(s => ({ ...s, inprogress: p }))}
      onPageSizeChange={setPageSize}
      testId="inprogress-pagination"
      itemLabel="job"
    />
  </>
);

export default InProgressJobsTab;
